import React, { FC, useEffect, useRef, useState } from "react";

// Hooks
import { useFormUI } from "./Hooks/form";
import { getRootData } from "./Hooks/useStepData";
import { useLabels } from "./Hooks/useLabels";

// Steps
import { FirstStep } from "./Steps/FormFirstStep";
import { SecondStep } from "./Steps/FormSecondStep";
import { ThirdStep } from "./Steps/FormThirdStep";
import { FourthStep } from "./Steps/FormFourthStep";

// Types
import { RootDataType } from "./Helpers/interfaces";
import { ServiceDto } from "@apiModels/ServiceDto.csharp";
import { SpecializationDto } from "@apiModels/SpecializationDto.csharp";
import { LocationDto } from "@apiModels/LocationDto.csharp";

// Elements
import { Summary } from "./Summary/Summary";
import { GoBackButton } from "./GoBackButton/GoBackButton";
import { RichTextArea } from "./RichTextArea/RichTextArea";
import { LoadingAnimation } from "../LoadingAnimation/LoadingAnimation";
import classnames from "classnames";
import { StorageAccessor } from "./StorageAccesor/StorageAccesor";
import { isObjectEmpty } from "./Helpers";
import { FormFields, FormReducer } from "./Helpers/formTypes";
import { usePersistedSelection } from "./Hooks/usePersistedSelection";
import Modal from "react-modal";
import { GtmService } from "./GtmService";
import { useBookingContext } from "../BookingButton/BookingContext/BookingContext";
import { ServiceGroupDto } from "@apiModels/ServiceGroupDto.csharp";

export const Booking: FC<{
  showCloseButton?: boolean;
  closeModal: () => void;
  autostartBooking: boolean;
  ignorePersistedData?: boolean;
}> = ({
  showCloseButton,
  closeModal,
  autostartBooking,
  ignorePersistedData,
}) => {
  const [rootData, setRootData] = useState<RootDataType>({
    centers: [],
    services: [],
    serviceGroups: [],
    specializations: [],
    doctors: [],
  });

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<any>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);

  const { state, dispatch, currentStep, nextStep, previousStep, setStep } =
    useFormUI();

  const currentStepRef = useRef(currentStep);
  const { bookingLabels } = useLabels();
  const { waitlist, awaitTime, filterValues } = useBookingContext();

  const modalOpenedFromWaitList = currentStep == 3 && waitlist?.contentId;

  useEffect(() => {
    if (error != null) {
      throw new Error(error);
    }
  }, [error]);

  useEffect(() => {
    (async function () {
      try {
        if (waitlist?.contentId) {
          handleStepChange(3);
          setLoading(false);
        } else {
          const { specializations, services, serviceGroups, centers, doctors } =
            await getRootData();
          setRootData({
            specializations,
            services,
            serviceGroups,
            centers,
            doctors,
          });

          if (
            ignorePersistedData ||
            isObjectEmpty(new StorageAccessor().getItem())
          ) {
            const {
              specializationId,
              serviceId,
              serviceGroupId,
              doctorId,
              locationId,
            } = filterValues ?? {};

            let service: ServiceDto | undefined;
            if (serviceId) {
              [service] = services.filter((s) => s.id == serviceId);
              dispatch({
                type: FormReducer.update,
                field: FormFields.service,
                payload: service,
              });
            }

            let specialization: SpecializationDto | undefined;
            if (specializationId) {
              [specialization] = specializations.filter(
                (s) => s.id == specializationId
              );
              dispatch({
                type: FormReducer.update,
                field: FormFields.specialization,
                payload: specialization,
              });
            }

            if (doctorId) {
              const [doctor] = doctors.filter((d) => d.id == doctorId);
              dispatch({
                type: FormReducer.update,
                field: FormFields.doctor,
                payload: doctor,
              });
            }

            let location: LocationDto | undefined;
            if (locationId) {
              [location] = centers.filter((c) => c.id == locationId);
              dispatch({
                type: FormReducer.update,
                field: FormFields.location,
                payload: location,
              });
            }

            let serviceGroup: ServiceGroupDto | undefined;
            if (serviceGroupId) {
              [serviceGroup] = serviceGroups.filter(
                (s) => s.id == serviceGroupId
              );
              dispatch({
                type: FormReducer.update,
                field: FormFields.serviceGroup,
                payload: serviceGroup,
              });
            }

            GtmService.bookingOverlayLoaded(
              specialization?.name,
              specialization?.id,
              service?.bookingName,
              service?.id,
              location?.name,
              location?.id
            );
          }
        }
      } catch (err: any) {
        setError(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    GtmService.overlayOpened();
    window.history.pushState(
      null,
      `form-step-${currentStepRef.current}`,
      window.location.pathname + window.location.search
    );
    window.addEventListener("popstate", onBackButtonEvent);
    return () => {
      window.removeEventListener("popstate", onBackButtonEvent);
    };
  }, []);

  useEffect(() => {
    if (!waitlist) {
      dispatch({
        type: FormReducer.update,
        field: FormFields.currentStep,
        payload: currentStep,
      });
    }
  }, [currentStep]);

  if (!ignorePersistedData) {
    usePersistedSelection(dispatch, setStep, waitlist);
  }

  useEffect(() => {
    const element = titleRef;
    if (element && element.current && !modalOpenedFromWaitList) {
      element.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [currentStep]);

  function onBackButtonEvent(e: PopStateEvent) {
    e.preventDefault();
    if (autostartBooking || !showCloseButton) {
      window.history.back();
      return;
    }

    closeModal();
  }

  function handleStepChange(step: number) {
    if (currentStep == 4) return;

    currentStepRef.current = step;
    switch (step) {
      case 1:
        currentStepRef.current = 1;
        setStep(1);
        break;
      case 2:
        if (state.specialization.id && state.service.id) {
          currentStepRef.current = 2;
          setStep(2);
        }
        break;
      case 3:
        if (
          (state.appointment.startTime && state.appointment.serviceId) ||
          (waitlist && waitlist.contentId)
        ) {
          currentStepRef.current = 3;
          setStep(3);
        }
        break;
      case 4:
        if (state.bookingResponse.bookingStatus != -1) {
          currentStepRef.current = 4;
          setStep(4);
        }
        break;
      default:
        break;
    }
  }

  function determineOptionalText(step: number): string | null | undefined {
    switch (step) {
      case 1:
        const labels = bookingLabels?.stepsBookingLabels.firstStepLabels;
        return filterValues?.doctorId
          ? labels?.optionalDescriptionDoctors
          : labels?.optionalDescription;
      default:
        return null;
    }
  }

  const determinedHeading = waitlist
    ? currentStep == 4
      ? "Registrering"
      : "Sett deg i kø"
    : bookingLabels?.commonLabels.bookingHeading ?? "Timebestilling";

  const shouldDisplaySummary =
    currentStep != 4 &&
    currentStep != 1 &&
    state.service.id &&
    state.specialization.id;

  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    Modal.setAppElement(wrapperRef.current!);
  }, []);

  return (
    <div>
      {currentStep != 1 && currentStep != 4 && !waitlist && (
        <GoBackButton
          onClick={previousStep}
          buttonLabel={bookingLabels?.commonLabels.goBackButtonLabel}
        />
      )}
      <div className="react-modal__top-helper d-sm-none" />

      <div className="page-boundary page-boundary--booking-inner">
        <div className="mt-3">
          <div className="steps-indicator d-flex justify-content-center mt-1 mb-3">
            <div className="d-flex flex-column">
              <h1
                ref={titleRef}
                className={
                  waitlist
                    ? "mb-2 mb-md-2 text-center"
                    : "mb-3 mb-md-4 text-center"
                }
              >
                {determinedHeading}
              </h1>
              {!waitlist && (
                <div className="d-flex justify-content-center">
                  {[1, 2, 3, 4].map((step: number) => (
                    <button
                      className={classnames("steps-indicator__step", "mx-1", {
                        active: currentStep == step,
                        nonreturnable: currentStep < step || currentStep == 4,
                      })}
                      key={`step-${step}`}
                      onClick={() => handleStepChange(step)}
                    >
                      <span className="visually-hidden">Steg </span>
                      {step}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {loading && (
            <LoadingAnimation
              className="loading-animation-container text-center"
              text="Laster inn"
            />
          )}
          {currentStep == 1 && !loading && (
            <FirstStep
              state={state}
              dispatch={dispatch}
              nextStep={nextStep}
              rootData={rootData}
            />
          )}
          {currentStep == 2 && !loading && (
            <SecondStep
              state={state}
              dispatch={dispatch}
              nextStep={nextStep}
              rootData={rootData}
            />
          )}
          {currentStep == 3 && !loading && (
            <ThirdStep
              state={state}
              dispatch={dispatch}
              nextStep={nextStep}
              rootData={rootData}
            />
          )}
          {currentStep == 4 && <FourthStep state={state} rootData={rootData} />}

          {shouldDisplaySummary && !loading && (
            <Summary
              state={state}
              step={currentStep}
              rootData={rootData}
              modal={Modal}
            />
          )}
        </div>
        {!loading && (
          <RichTextArea
            innerClassName="mb-3 booking-narrow-item"
            content={determineOptionalText(currentStep)}
          />
        )}
      </div>
    </div>
  );
};
